import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CallIntelHooks } from '@frontend/api-call-intel';
import { checkAggregateACL, getUser, isWeaveUser } from '@frontend/auth-helpers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { CallIntelWidgetFFMap } from '../constants';
import { CallIntelDashboardWidgetType } from '../types';
import { useShowCallIntelWidgetsDemo } from './useShowCallIntelWidgetsDemo';

interface UseCallIntelDashWidgetVisibilityProps {
  widgetId: CallIntelDashboardWidgetType;
  demoEnabled?: boolean;
}

export const useCallIntelDashWidgetVisibility = ({ widgetId }: UseCallIntelDashWidgetVisibilityProps) => {
  const { getFeatureFlagLocationMap } = useScopedAppFlagStore();
  const { selectedLocationIds } = useAppScopeStore();
  const user = getUser();

  // User type checks
  const isWeaveDemoUser = user?.username.toLowerCase().includes('@demoweave');
  const isWeaveInternalUser = isWeaveUser() || isWeaveDemoUser;

  // Check Call Intelligence access
  const hasCallIntelAccess = CallIntelHooks.useHasCallIntelFeature();

  // Feature flag checks
  const widgetFeatureFlag = CallIntelWidgetFFMap[widgetId];
  const widgetFeatureFlagMap = getFeatureFlagLocationMap(widgetFeatureFlag);

  // Permission checks
  const enabledLocationIds = selectedLocationIds.filter((locationId) => widgetFeatureFlagMap[locationId]?.value);

  const { aclValuesByLocationId } = checkAggregateACL(selectedLocationIds, [Permission.CALL_CLASSIFICATION_READ]);

  const locationIdsWithPermission = isWeaveInternalUser
    ? enabledLocationIds
    : enabledLocationIds.filter((locationId) => aclValuesByLocationId[locationId]);

  // Demo Checks
  const showDemo = useShowCallIntelWidgetsDemo();

  // Visibility conditions
  const showWidget = (showDemo || hasCallIntelAccess) && locationIdsWithPermission.length > 0;

  return {
    showWidget,
    locationIdsWithPermission,
    showDemo,
  };
};
